import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material'
import {
  Close as CloseIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material'
import dayjs from 'dayjs'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { CertificateIcon, PdfIcon } from 'src/v3/core/icons'
import {
  selectDayActivitiesModal,
  selectDownloadState,
  setDayActivitiesOpen,
  setSelectedUserForDetails,
  setUserDetailsOpen,
  setSelectedAssignmentEvent,
  setAssignmentEventOpen,
} from '../../index'

type Props = {
  onDownloadCertificate: (
    certificateId: string,
    activityType: string,
    fileName: string,
  ) => void
}

export default function DayActivitiesModal({ onDownloadCertificate }: Props) {
  let theme = useTheme()
  let dispatch = useAppDispatch()

  // Redux selectors
  let dayActivitiesModal = useAppSelector(selectDayActivitiesModal)
  let downloadState = useAppSelector(selectDownloadState)

  let { selectedDayData } = dayActivitiesModal

  let handleClose = () => {
    dispatch(setDayActivitiesOpen(false))
  }

  let handleUserClick = (userId: string) => {
    dispatch(setSelectedUserForDetails(userId))
    dispatch(setUserDetailsOpen(true))
  }

  let handleAssignmentClick = (assignmentEvent: any) => {
    dispatch(setSelectedAssignmentEvent(assignmentEvent))
    dispatch(setAssignmentEventOpen(true))
  }

  let handleDownload = (activity: any) => {
    if (activity.certificateId) {
      let fileName =
        `${activity.userName}_${activity.courseTitle}_${dayjs(activity.completionDate).format('YYYY-MM-DD')}`.replace(
          /[^a-zA-Z0-9]/g,
          '_',
        )
      onDownloadCertificate(
        activity.certificateId,
        activity.activityType,
        fileName,
      )
    }
  }

  if (!dayActivitiesModal.dayActivitiesOpen || !selectedDayData) {
    return null
  }

  return (
    <Dialog
      open={dayActivitiesModal.dayActivitiesOpen}
      onClose={handleClose}
      fullWidth
      slotProps={{
        paper: {
          sx: { bgcolor: 'background.paper' },
        },
      }}
      maxWidth="lg"
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'background.paper',
        }}
      >
        <Box>
          <Typography color="text.primary" variant="h6">
            Activities for {dayjs(selectedDayData.date).format('MMMM DD, YYYY')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {dayjs(selectedDayData.date).format('dddd')} •{' '}
            {selectedDayData.totalHours} CE Hours Total
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ color: 'text.primary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ bgcolor: 'background.paper' }}>
        {/* Day Summary */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip
              label={`${selectedDayData.activities.length} CE Activities`}
              color="primary"
              variant="outlined"
            />
            <Chip
              label={`${selectedDayData.assignmentEvents.length} Assignment Events`}
              color="secondary"
              variant="outlined"
            />
            <Chip
              label={`${selectedDayData.totalHours} Total Hours`}
              color="success"
              variant="outlined"
            />
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Assignment Events */}
        {selectedDayData.assignmentEvents.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography
              color="text.primary"
              variant="h6"
              sx={{
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <AssignmentIcon />
              Assignment Events
            </Typography>

            {selectedDayData.assignmentEvents.map((assignmentEvent, index) => (
              <Card
                key={assignmentEvent.id || index}
                variant="outlined"
                sx={{
                  mb: 2,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: theme.shadows[4],
                    transform: 'translateY(-2px)',
                    borderColor: 'secondary.main',
                  },
                }}
                onClick={() => handleAssignmentClick(assignmentEvent)}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: 'secondary.main',
                        }}
                      >
                        Assignment Created: {assignmentEvent.assignmentName}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        {assignmentEvent.userCount} users •{' '}
                        {assignmentEvent.courseCount} courses
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Due:{' '}
                        {dayjs(assignmentEvent.dueDate).format('MMM DD, YYYY')}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label="Assignment"
                      color="secondary"
                      variant="filled"
                    />
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {/* CE Activities */}
        <Typography
          color="text.primary"
          variant="h6"
          sx={{
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <CertificateIcon />
          CE Activities
        </Typography>

        {selectedDayData.activities.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center', py: 4 }}
          >
            No CE activities completed on this day.
          </Typography>
        ) : (
          <List>
            {selectedDayData.activities.map((activity, index) => (
              <ListItem
                key={activity.id || index}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: theme.shadows[2],
                    borderColor: 'primary.main',
                  },
                }}
                onClick={() => handleUserClick(activity.userId)}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    width: '100%',
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Typography variant="subtitle2">
                            {activity.courseTitle}
                          </Typography>
                          <Chip
                            size="small"
                            label={`${activity.ceHours}h`}
                            color="primary"
                            variant="outlined"
                          />
                          <Chip
                            size="small"
                            label={activity.activityType}
                            color={
                              activity.activityType === 'recorded'
                                ? 'info'
                                : activity.activityType === 'live'
                                  ? 'warning'
                                  : 'default'
                            }
                            variant="filled"
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            <strong>User:</strong> {activity.userName} •{' '}
                            <strong>Category:</strong> {activity.category}
                          </Typography>
                          {activity.courseCode && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 0.5 }}
                            >
                              <strong>Course Code:</strong>{' '}
                              {activity.courseCode}
                            </Typography>
                          )}
                          {activity.wasAssigned && (
                            <Box sx={{ mt: 1 }}>
                              <Chip
                                size="small"
                                label={`Assignment: ${activity.assignmentName}`}
                                color="secondary"
                                variant="outlined"
                              />
                              {activity.assignmentDueDate && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ ml: 1 }}
                                >
                                  Due:{' '}
                                  {dayjs(activity.assignmentDueDate).format(
                                    'MMM DD, YYYY',
                                  )}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  </Box>

                  {/* Certificate Download */}
                  {activity.certificateId && (
                    <Box sx={{ ml: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={
                          downloadState.downloading ===
                          activity.certificateId ? (
                            <div>⏳</div>
                          ) : (
                            <PdfIcon />
                          )
                        }
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDownload(activity)
                        }}
                        disabled={
                          downloadState.downloading === activity.certificateId
                        }
                        sx={{ minWidth: 'auto' }}
                      >
                        {downloadState.downloading === activity.certificateId
                          ? 'Downloading...'
                          : 'Certificate'}
                      </Button>
                    </Box>
                  )}
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ bgcolor: 'background.paper' }}>
        <Button onClick={handleClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
